import { useMemo } from 'react';
import Fuse, { IFuseOptions } from 'fuse.js';

export type FuzzyKey<T> = keyof T | string | { name: string; weight?: number };

export interface UseFuzzyOptions<T> {
  threshold?: number;
  ignoreLocation?: boolean;
  minMatchCharLength?: number;
  shouldSort?: boolean;
  extra?: Partial<IFuseOptions<T>>;
}

const DEFAULTS = {
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2,
  shouldSort: true,
};

export function useFuzzy<T>(
  items: readonly T[] | undefined | null,
  keys: FuzzyKey<T>[],
  query: string,
  opts: UseFuzzyOptions<T> = {},
): T[] {
  const list = items ?? [];
  const fuse = useMemo(() => {
    const options: IFuseOptions<T> = {
      keys: keys as IFuseOptions<T>['keys'],
      threshold: opts.threshold ?? DEFAULTS.threshold,
      ignoreLocation: opts.ignoreLocation ?? DEFAULTS.ignoreLocation,
      minMatchCharLength: opts.minMatchCharLength ?? DEFAULTS.minMatchCharLength,
      shouldSort: opts.shouldSort ?? DEFAULTS.shouldSort,
      includeScore: false,
      ...opts.extra,
    };
    return new Fuse(list as T[], options);
  }, [list, keys, opts.threshold, opts.ignoreLocation, opts.minMatchCharLength, opts.shouldSort, opts.extra]);

  return useMemo(() => {
    const q = (query || '').trim();
    if (q.length < (opts.minMatchCharLength ?? DEFAULTS.minMatchCharLength)) return list as T[];
    return fuse.search(q).map((r) => r.item);
  }, [fuse, query, list, opts.minMatchCharLength]);
}

export function fuzzyFilter<T>(
  items: readonly T[],
  keys: FuzzyKey<T>[],
  query: string,
  opts: UseFuzzyOptions<T> = {},
): T[] {
  const q = (query || '').trim();
  const min = opts.minMatchCharLength ?? DEFAULTS.minMatchCharLength;
  if (q.length < min) return items as T[];
  const fuse = new Fuse(items as T[], {
    keys: keys as IFuseOptions<T>['keys'],
    threshold: opts.threshold ?? DEFAULTS.threshold,
    ignoreLocation: opts.ignoreLocation ?? DEFAULTS.ignoreLocation,
    minMatchCharLength: min,
    shouldSort: opts.shouldSort ?? DEFAULTS.shouldSort,
    includeScore: false,
    ...opts.extra,
  });
  return fuse.search(q).map((r) => r.item);
}
